import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { KEYBOARD } from '../../../config/constants';
import { blockScroll, debounce } from '../../../services/utils';

@Component({
  tag: 'pk-bottom-panel',
  styleUrl: './scss/pk-bottom-panel.scss',
  shadow: true,
})
export class PkBottomPanel {
  // Own Properties
  // ====================

  private timeout: number = 250;
  private bottomPanel: HTMLDivElement;
  private overlayElem: HTMLDivElement;
  private wrapperElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkBottomPanelElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private maxHeight: number;

  // Public Property API
  // ========================

  /** If true, the page body behind the bottom panel will be scrollable */
  @Prop({ reflect: true }) allowWindowScroll: boolean = false;

  /** If true, clicking the modal overlay won't close it */
  @Prop({ reflect: true }) disableOverlayClose: boolean = false;

  /** If true, the close button won't be showed in the panel */
  @Prop({ reflect: true }) hideCloseButton: boolean = false;

  /** If true, a screen overlay will be display blocking the rest of the functionality on the screen */
  @Prop({ reflect: true }) overlay: boolean = false;

  /** If true, the bottom panel will be focused when opened (default: same as overlay) */
  @Prop({ reflect: true }) focusOnOpen?: boolean = this.overlay;

  /** If true, the bottom panel will be shown at the bottom of the viewport */
  @Prop({ reflect: true, mutable: true }) opened: boolean = false;

  /** Specify the width size for the panel to use */
  @Prop({ reflect: true }) size: 'small' | 'medium' | 'large' | 'full' = 'small';

  /** Aria label for the bottom panel */
  @Prop({ reflect: true }) panelAriaLabel: string;

  // Prop lifecycle events
  // =======================

  @Watch('opened')
  onOpenPropChange() {
    this.opened && this.handleFocus();
    // Block the body scroll when the bottom panel is opened and the overlay is enabled
    if (!this.allowWindowScroll && this.overlay) {
      blockScroll(this.opened, 'bottom-panel');
    }
  }

  @Watch('size')
  onSizePropChange() {
    if (!this.bottomPanel) return;

    if (this.size === 'full') {
      this.handleFullWidth();
      return;
    }

    this.bottomPanel.style.removeProperty('width');
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the bottom panel will close */
  @Event({ cancelable: true, bubbles: false }) pkClose: EventEmitter;

  /** Callback handler to be called when the bottom panel will open */
  @Event({ cancelable: true, bubbles: false }) pkOpen: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.setMaxHeight();
    this.handleFullWidth();
    this.onOpenPropChange();
    this.onSizePropChange();

    this.overlayElem?.addEventListener('click', this.handleOverlayClick);
  }

  disconnectedCallback() {
    this.overlayElem?.removeEventListener('click', this.handleOverlayClick);
  }

  // Listeners
  // ==============

  @Listen('resize', { target: 'window' })
  onWindowResize() {
    this.setMaxHeight();
    this.handleFullWidth();
  }

  @Listen('keydown', { target: 'document' })
  handleDocumentKeyDown(event: KeyboardEvent) {
    if (event.key !== KEYBOARD.Escape || !this.opened) return;

    this.close();
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Open the bottom panel */
  @Method()
  async open(): Promise<void> {
    this.openHandler();
  }

  /** Close the bottom panel */
  @Method()
  async close(): Promise<void> {
    this.closeHandler();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private closeHandler = () => {
    const ev = this.pkClose.emit();
    if (!ev.defaultPrevented) {
      this.opened = false;
    }
  };

  private openHandler = () => {
    const ev = this.pkOpen.emit();
    if (!ev.defaultPrevented) {
      this.opened = true;
    }
  };

  private setMaxHeight = debounce(() => {
    this.maxHeight = this.bottomPanel.scrollHeight;
    this.bottomPanel.style.bottom = `-${this.maxHeight}px`;
  }, this.timeout);

  private handleFocus = () => {
    if (!this.focusOnOpen) return;

    setTimeout(async () => {
      // Details: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus#preventscroll
      this.bottomPanel.focus({ preventScroll: true });
    }, this.timeout);
  };

  private handleFullWidth = debounce(() => {
    if (this.size !== 'full') return;
    if (!this.wrapperElem || !this.bottomPanel) return;

    if (window.innerWidth < 768) {
      this.bottomPanel.style.width = '100%';
      return;
    }

    this.bottomPanel.style.width = `${this.wrapperElem.clientWidth}px`;
  }, 150);

  private handleOverlayClick = () => {
    if (!this.overlay || this.disableOverlayClose) return;

    this.closeHandler();
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host class="pk-d--block pk-py--0">
        <div
          class="mx-auto my-0 flex justify-end"
          ref={(div: HTMLDivElement) => (this.wrapperElem = div)}
          part="wrapper"
        >
          <div
            class={{ 'pk-bottom-panel__overlay': true, hidden: !(this.opened && this.overlay) }}
            ref={(div) => (this.overlayElem = div)}
            part="overlay"
          />
          <div
            class={{ 'pk-bottom-panel': true, open: this.opened, [`${this.size}`]: true }}
            ref={(el: HTMLDivElement) => (this.bottomPanel = el)}
            aria-modal="true"
            aria-label={this.panelAriaLabel}
            role="dialog"
            part="base"
            tabIndex={this.opened && this.focusOnOpen ? 0 : -1}
          >
            <div class="flex">
              <div class="flex flex-1 flex-col" part="content">
                <div class="title" part="title">
                  <slot name="title" />
                </div>
                <div class="body" part="body">
                  <slot />
                </div>
              </div>
              {!this.hideCloseButton && (
                <pk-button
                  class="pk-bottom-panel__close"
                  type="button"
                  appearance="icon"
                  onPkClick={this.closeHandler}
                  aria-label="close"
                  part="close-button"
                  soft
                >
                  <pk-icon name="navigation-close" role="img" title="close" part="close-button-icon" />
                </pk-button>
              )}
            </div>
            <div class="mt-m" part="actions">
              <slot name="actions" />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
